import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import "./EstimateBottomSheet.css";

/**
 * Shared focused-entry surface for Estimate workspace actions (Add Location,
 * Add Feature, Add/Edit Measurement, etc.) — a compact bottom sheet rather
 * than a giant inline form. Mirrors the existing WorkTypeNameDialog's proven
 * portal + body-scroll-lock pattern so all Estimate modals behave the same
 * way (Escape to close, overlay click to close, focus trap not required
 * since content is short-lived).
 */
export function EstimateBottomSheet({
  title,
  subtitle,
  children,
  onClose,
  primaryLabel = "Save",
  onPrimary,
  primaryDisabled = false,
  primaryBusy = false,
  secondaryLabel = "Cancel",
  onSecondary,
  error,
}) {
  useBodyScrollLock(true);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape" && !primaryBusy) {
        onClose?.();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, primaryBusy]);

  function handleOverlayClick(event) {
    if (event.target === event.currentTarget && !primaryBusy) {
      onClose?.();
    }
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="est-sheet-overlay" onClick={handleOverlayClick}>
      <div
        className="est-sheet-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="est-sheet-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="est-sheet-handle" aria-hidden="true" />
        <div className="est-sheet-header">
          <div id="est-sheet-title" className="est-sheet-title">
            {title}
          </div>
          {subtitle ? <div className="est-sheet-subtitle">{subtitle}</div> : null}
        </div>

        <div className="est-sheet-body">{children}</div>

        {error ? <div className="est-sheet-error">{error}</div> : null}

        <div className="est-sheet-actions">
          <button
            type="button"
            className="est-sheet-btn est-sheet-btn--secondary"
            onClick={onSecondary || onClose}
            disabled={primaryBusy}
          >
            {secondaryLabel}
          </button>
          {onPrimary ? (
            <button
              type="button"
              className="est-sheet-btn est-sheet-btn--primary"
              onClick={onPrimary}
              disabled={primaryDisabled || primaryBusy}
            >
              {primaryBusy ? "Saving…" : primaryLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
